import { AppInfo } from '../constant';
import { getSyncSiteMetadata } from '../metaData';
import { Account, initOptions, PROVIDER_ALLIANCE } from '../types/types';
import ProxyLocalStorage from '../ProxyLocalStorage';
import EventEmitter from 'eventemitter3';
import axios from 'axios';
import { errorCodes, messages, rpcErrors } from '../message';
import { tgUtils } from '../utils/utils';
import {
  loginDefineProperties,
  signDefineProperties,
} from '../utils/properties';
interface RequestOptions {
  account: Account;
  metaData: initOptions['metaData'];
  timeStamp?: number;
}

function transfer16(val: string | number = 0) {
  val = isNaN(Number(val)) ? 1 : Number(val);
  return '0x' + val.toString(16);
}

/** for clear walletPromise when timeout or promise finally */
const walletPromiseTimeoutClear = (() => {
  const walletPromiseMap: Map<string, Promise<Record<string, any>>> = new Map();

  return (id, fn, timeout = 1000) => {
    if (walletPromiseMap.has(id)) return walletPromiseMap.get(id);
    const promise: Promise<Record<string, any>> = fn();
    walletPromiseMap.set(id, promise);
    setTimeout(() => {
      walletPromiseMap.delete(id);
    }, timeout);
    return promise.finally(() => {
      walletPromiseMap.delete(id);
    });
  };
})();

const defaultAccount: Account = {
  address: '',
  chainId: '0x1',
  chainKey: 'ethereum',
  alliance: PROVIDER_ALLIANCE.EVM,
  chainName: 'Ethereum netWork',
  chainSymbol: 'ETH',
};

export default class ProviderUtils extends EventEmitter {
  eventTimeout: number;
  storage: ProxyLocalStorage;
  metaData: initOptions['metaData'];
  connectUrl: string;
  bridgeUrl: string;
  connect_direct_link: string;
  private _isRetryCount: number = 0;
  get _lastTime() {
    return isNaN(Number(this.storage.get('lastTime') || 0))
      ? 0
      : Number(this.storage.get('lastTime') || 0);
  }
  set _lastTime(value) {
    this.storage.set('lastTime', value);
  }

  get _account() {
    return this.storage.get('account') || defaultAccount;
  }
  set _account(account: Account | null) {
    const oldAccount = { ...(this._account || defaultAccount) };

    if (!account) {
      account = {
        ...oldAccount,
        address: '',
      };
    }
    this.storage.set('account', account);

    if (transfer16(oldAccount?.chainId) != transfer16(account?.chainId)) {
      this.emit('chainChanged', this?.chainId, account?.alliance, account);
      this.emit('networkChanged', parseInt(this?.chainId), account?.alliance);
    }
    if (oldAccount?.address != account?.address) {
      this.emit('accountsChanged', account?.address ? [account?.address] : []);
    }
  }

  get _accounts() {
    return (
      this.storage.get(`accounts`) || {
        ethereum: defaultAccount,
      }
    );
  }
  set _accounts(accounts: { [key: string]: Account }) {
    this.storage.set(`accounts`, accounts);
  }

  get chainId() {
    return this._account?.chainId ? transfer16(this._account?.chainId) : null;
  }

  constructor(options?: initOptions) {
    super();
    this.eventTimeout = options?.eventTimeout || 10 * 60 * 1000;
    this.metaData = options?.metaData;
    this.storage = new ProxyLocalStorage();
    this.connectUrl = options.connect;
    this.bridgeUrl = options.bridge;
    this.connect_direct_link = options.connect_direct_link;
  }

  async request(payload: { method: string; params: any[] }) {
    const { method, params = [] } = payload || {};

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      throw rpcErrors.invalidRequest({
        message: messages.errors.invalidRequestArgs(),
        data: params,
      });
    }

    if (typeof method !== 'string' || method.length === 0) {
      throw rpcErrors.invalidRequest({
        message: messages.errors.invalidRequestMethod(),
        data: payload as any,
      });
    }

    const requestKey = `${method}-${JSON.stringify(params || [])}`;
    return walletPromiseTimeoutClear(requestKey, () =>
      this.connectSse(method, params)
    );
  }

  /** get sse token / add salt */
  async getSseToken(method, params, options?: RequestOptions) {
    const account = this._account;
    const now = new Date().getTime();
    this._lastTime = now;
    options = {
      account,
      metaData: getSyncSiteMetadata(this.metaData),
      ...options,
      timeStamp: now,
    };
    const id =
      new Date().getTime() + Math.floor(Math.random() * 1000).toString();

    let salt = `salt-${Date.now()}-${id}`;
    salt = Buffer.from(salt, 'utf-8')
      .toString('base64')
      .replace('==', '')
      .replace('=', '');

    const payload = {
      id,
      method,
      params,
      options,
    };

    // push参数
    const publish_params = {
      id: payload.id,
      data: payload,
      version: '1.0',
      salt: salt,
    };
    const response = await axios.post(
      this.connectUrl,
      {
        payload_base64: Buffer.from(
          JSON.stringify(publish_params),
          'utf-8'
        ).toString('base64'),
      },
      {
        headers: {
          'X-Salt': salt,
        },
      }
    );

    const { hash, signature } = response.data;
    return { hash, signature, salt };
  }

  /** connect sse */
  async connectSse(method, params, options?: RequestOptions) {
    // add salt
    const { salt, hash, signature } = await this.getSseToken(
      method,
      params,
      options
    );
    // deepLink param
    const direct_params = {
      method,
      params: [salt, hash, signature],
    };
    const id =
      new Date().getTime() + Math.floor(Math.random() * 1000).toString();
    const request = {
      id,
      method,
      params,
      options,
      hash,
      signature,
      salt,
    };
    return this.createEventSource(
      hash,
      signature,
      salt,
      direct_params,
      request
    );
  }

  /** create EventSource */
  createEventSource(hash, signature, salt, direct_params, request) {
    const eventSource = new EventSource(
      `${this.bridgeUrl}?appid=${hash}&signature=${signature}&salt=${salt}`
    );
    return new Promise((resolve, reject) => {
      const timer =
        this.eventTimeout > 0
          ? setTimeout(() => {
              reject(
                rpcErrors.invalidRequest({
                  code: errorCodes?.rpc?.timeoutRequest,
                  message: messages.errors.timeOut(request.method),
                  data: request as any,
                })
              );
              eventSource.close();
            }, this.eventTimeout || 60000)
          : null;

      eventSource.addEventListener('message', event => {
        if (event?.data === 'hi' || event?.data === 'close') return;
        if (!event?.data) {
          this._isRetryCount += 1;
          eventSource.close();
          clearTimeout(timer);
          if (this._isRetryCount > 4) {
            this._isRetryCount = 0;
            throw new Error('Tomo Wallet Request error');
          }
          // retry, max 4 times
          return this.createEventSource(
            hash,
            signature,
            salt,
            direct_params,
            request
          );
        }

        const [chain, operation] = request.method.split('_');

        try {
          const decodedString = atob(event?.data);
          let data = JSON.parse(decodedString);

          const source = this.storage.get('account');

          if (operation.includes('connect')) {
            const isTonProof = request.params[0]?.tonProof;
            data = loginDefineProperties(request, data, chain.toUpperCase(), isTonProof);
            this.storage.set('account', data.options.account);
            // add useId in storage key
            this.storage.set(`accounts`, {
              ...(this.storage.get(`accounts`) || {}),
              [chain.toLowerCase()]: data.options.account,
            });
          } else {
            data = signDefineProperties(request, data, source, chain);
          }

          if (data?.id == request.id || salt == data?.salt) {
            eventSource?.close();

            clearTimeout(timer);
            if (data.reConnect || !data.error) {
              ProxyResponse.call(this, data);
            }

            this._isRetryCount = 0;
            data.error ? reject(data.error) : resolve({ ...data.result });
            return;
          } else {
            return console.log('Tomo wallet data error');
          }
        } catch (error) {
          console.log(error);
          reject(error)
        }
      });

      this.openAuthBot(direct_params);
    });
  }

  /** open auth bot */
  openAuthBot(direct_params: { method: string; params: any[] }) {
    let base64Content = { method: direct_params.method, params: direct_params.params };
    let base64ContentStr = JSON.stringify(base64Content);
    const utf8Array = new TextEncoder().encode(base64ContentStr);
    const base64Encoded = btoa(String.fromCharCode(...utf8Array));

    if (!this._isRetryCount) {
      const Telegram = window.Telegram;
      // check is tg web app sdk available
      if (!Telegram) {
        throw `[TOMO] please include https://telegram.org/js/telegram-web-app.js in your project`
      }
      const url = `${
        this.connect_direct_link
      }?startapp=${base64Encoded}&mode=compact`;
      Telegram.WebApp.initData && Telegram?.WebApp?.openTelegramLink?.(url);

      if (!Telegram.WebApp.initData) {
        const match = this.connect_direct_link.match(
          /t\.me\/([^\/]+)\/([^\/]+)/
        );
        if (match[1] && match[2]) {
          !Telegram.WebApp.initData &&
            tgUtils.opendeepLink(base64Encoded, {
              domain: match[1],
              appname: match[2],
            });
        } else {
          !Telegram.WebApp.initData && tgUtils.opendeepLink(base64Encoded, {});
        }
      }
    }
  }

  /** get tomo bot app infp */
  getAppInfo() {
    return {
      ...AppInfo,
    };
  }

  removeAccount(chainType) {
    this._accounts = { ...this._accounts, [chainType]: null };
  }

  getMethodInfo(methodString: string) {
    return methodString.split('_') || [];
  }
}

function ProxyResponse(reponsePayload) {
  const { method, config, params, result } = reponsePayload || {};
  const requestParams = config?.params?.[0] || {};
  if (config) {
    // console.log('ProxyResponse', reponsePayload);
    const accounts = config?.accounts;
    accounts && (this._accounts = { ...(this._accounts || {}), ...accounts });

    return reponsePayload;
  }
}
